import React from "react";
import {Box, Stack, Tooltip, Typography} from "@mui/material";
import CheckIcon from "~/icons/svg/CheckIcon.svg?react";
import {CONSOLIDATION_TOOL_NAMES} from "~/pages/chat-agent/types/chatTypes";
import type {ChatMessage} from "~/pages/chat-agent/types/chatTypes";
import {ConsolidationProgress} from "~/pages/chat-agent/components/AgentActivity/ConsolidationProgress";
import {
    spinnerSx,
    stepDurationSx,
    stepLabelSx,
    stepMarkerSx,
    toolNameSx,
    toolRowSx,
    waitDotSx,
} from "~/pages/chat-agent/components/AgentActivity/agentActivity.styles";

export type ToolCallStatus = "running" | "done" | "no_response";

const STATUS: Record<ToolCallStatus, {label: string; marker: React.ReactNode}> = {
    running: {label: "Running", marker: <Box sx={spinnerSx}/>},
    done: {label: "Response received", marker: <CheckIcon width={14} height={14}/>},
    no_response: {label: "No response", marker: <Box sx={waitDotSx}/>},
};

type Props = {
    /** A function_call / function_response message; its content is the tool name. */
    call: ChatMessage;
    status: ToolCallStatus;
    /** What the step reads as, e.g. "Data retrieval"; the tool name shows on hover. */
    label: string;
};

/** One tool call as a step: a status marker, its label and, once answered, how long it took (e.g. `1.2s`). */
export const ToolCallRow: React.FC<Props> = React.memo(({call, status, label: stepLabel}) => {
    const {label, marker} = STATUS[status];
    const durationMs = status === "done" && call.respondedAt
        ? call.respondedAt.getTime() - call.timestamp.getTime()
        : undefined;
    return (
        <Box sx={toolRowSx}>
            <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                <Stack
                    component="span"
                    role="img"
                    aria-label={label}
                    alignItems="center"
                    justifyContent="center"
                    sx={stepMarkerSx[status]}
                >
                    {marker}
                </Stack>
                <Tooltip title={<Box component="span" sx={toolNameSx}>{call.content}</Box>}>
                    <Typography variant="body2" noWrap sx={stepLabelSx[status]}>
                        {stepLabel}
                    </Typography>
                </Tooltip>
                {durationMs != null && (
                    <Typography variant="caption" sx={stepDurationSx}>
                        {`${(Math.max(0, durationMs) / 1000).toFixed(1)}s`}
                    </Typography>
                )}
            </Stack>
            {status === "running" && CONSOLIDATION_TOOL_NAMES.has(call.content) && (
                <ConsolidationProgress progress={call.toolProgress}/>
            )}
        </Box>
    );
});
